import React from "react";

import { cn } from "~/common/redesign-v1/cn";
import { Card } from "~/common/redesign-v1/ui/card";

/**
 * Shared read-only presentation primitives for the redesign patient-view.
 * Label + plain value text (not an input-styled box) — values must not look editable.
 */

/**
 * A section sub-heading (e.g. "Информация о пациенте", "Оплата") with a full-width divider above it. `first` (the
 * card's very first heading) drops the divider and uses a smaller top offset instead — CSS `first:` can't
 * detect this reliably since `ViewCard`'s own title row is always an earlier sibling, so it's an explicit
 * prop (also fixes #4: the doubled top gap on "Карта пациента").
 */
export function ViewSubHeading({ className, children, first = false, ...props }: React.ComponentProps<"h3"> & { first?: boolean }) {
  return (
    <h3 className={cn("tw-mb-5 tw-text-2xl tw-font-normal tw-leading-[1.2] tw-text-foreground", first ? "tw-mt-2" : "tw-mt-6 tw-border-t tw-border-[#E5E5E5] tw-pt-3", className)} {...props}>
      {children}
    </h3>
  );
}

/** A label and plain read-only value without input chrome. */
export function ViewField({ label, children, className, valueTestId }: { label: React.ReactNode; children: React.ReactNode; className?: string; valueTestId?: string }) {
  return (
    <div className={cn("tw-mb-3 tw-flex tw-flex-col tw-gap-[5px]", className)}>
      <span className="tw-text-sm tw-font-semibold tw-text-foreground">{label}</span>
      {/* `whitespace-pre-wrap` alone leaves a single long unbroken
          token (a long URL/promo code in `logistics_comment`/`promo_code`, a long diagnosis word) free to
          overflow the card — `overflow-wrap: break-word` forces it to break inside the box instead. */}
      <div data-testid={valueTestId} className="tw-whitespace-pre-wrap tw-break-words tw-text-sm tw-text-foreground">
        {children}
      </div>
    </div>
  );
}

/** A titled Card section (used for "Карта пациента", "Пациент", "Статус оплаты", "Отправка слепков"). */
export function ViewCard({ title, action, children, testId, className }: { title: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; testId?: string; className?: string }) {
  return (
    <Card className={cn("tw-min-w-0 tw-rounded-2xl tw-p-5", className)} data-testid={testId}>
      <div className="tw-mb-4 tw-flex tw-items-start tw-justify-between tw-gap-3">
        <h2 className="tw-font-rd-accent tw-text-2xl tw-font-normal tw-leading-[1.2] tw-tracking-tight tw-text-foreground">{title}</h2>
        {action}
      </div>
      {children}
    </Card>
  );
}
